import { useEffect, useRef, useState } from 'react';
import { SearchingIcon } from '@icons';

import { inputStyles } from '@components/input/input.config';
import Icon from '../icon/Icon';

interface SearchBarProps {
  inputId?: string;
  placeholder: string;
  SearchIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  onSearch: (searchQuery: string) => void;
  searchBarStyle?: string;
  iconStyle?: string;
  searchInputStyle?: string;
  value?: string;
  clearSearchInput?: boolean;
  stopEventPropagation?: boolean;
  isSearchDigitsOnly?: boolean;
  debounceDelay?: number;
}

const SearchBar = ({
  inputId,
  placeholder,
  SearchIcon = SearchingIcon,
  onSearch,
  searchBarStyle,
  iconStyle,
  searchInputStyle,
  value = '',
  clearSearchInput = false,
  stopEventPropagation = false,
  isSearchDigitsOnly = false,
  debounceDelay = 1000
}: SearchBarProps) => {
  const [searchText, setSearchText] = useState<string>(value);

  // 🔑 debounce reference
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const triggerSearch = (query: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onSearch(query);
    }, debounceDelay);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: searchValue } = event.target;

    if (isSearchDigitsOnly && searchValue && !/^[0-9]+$/.test(searchValue)) {
      return;
    }

    setSearchText(searchValue);
    triggerSearch(searchValue);

    if (stopEventPropagation) {
      event.stopPropagation();
    }
  };

  const handleSearchBarClear = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    setSearchText('');
    onSearch('');
  };

  const onInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (stopEventPropagation) {
      event.stopPropagation();
    }
  };

  // Sync external value → local state
  useEffect(() => {
    setSearchText(value);
  }, [value]);

  // Clear search when triggered by parent
  useEffect(() => {
    if (clearSearchInput) {
      handleSearchBarClear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSearchInput]);

  // 🧹 Cleanup debounce on unmount
  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    []
  );

  return (
    <div className={`relative h-11 ${searchBarStyle || ''}`}>
      {SearchIcon && (
        <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
          <SearchIcon className={iconStyle || ''} />
        </div>
      )}

      <input
        data-testid={inputId}
        value={searchText}
        placeholder={placeholder}
        onChange={handleInputChange}
        onClick={onInputClick}
        type="text"
        className={`placeholder:text-text-grey size-full border-grey30 bg-white py-2 pl-10 pr-9 text-sm ${
          inputStyles.common
        } ${searchInputStyle || ''}`}
      />

      {searchText && (
        <div
          role="presentation"
          onClick={handleSearchBarClear}
          className="absolute inset-y-0 right-4 flex cursor-pointer items-center"
        >
          <Icon name="close" />
        </div>
      )}
    </div>
  );
};

export default SearchBar;
